import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import {
  documentCategories
} from '../data/corporateContent';
import { ROUTE_PATHS } from '../config/navigation';
import { hasPermission } from '../utils/roles';
import useDebouncedValue from '../hooks/useDebouncedValue';

const DOCUMENTS_PER_PAGE = 25;
const EMPTY_CATEGORY_COUNTS = Object.fromEntries(documentCategories.map((category) => [category.id, 0]));
const FILE_TYPE_VISUALS = {
  excel: { symbol: 'X', label: 'XLS' },
  word: { symbol: 'W', label: 'DOC' },
  powerpoint: { symbol: 'P', label: 'PPT' },
  pdf: { symbol: 'PDF', label: 'PDF' },
  image: { symbol: 'IMG', label: 'GÖR' },
  video: { symbol: '▶', label: 'VİD' },
  archive: { symbol: 'ZIP', label: 'ARŞ' },
  text: { symbol: 'TXT', label: 'TXT' },
  other: { symbol: '•', label: 'DOS' }
};

const getVisualFileType = (item) => {
  const filename = String(item.originalName || item.name || '');
  const extension = filename.includes('.') ? filename.split('.').pop().toLocaleLowerCase('tr-TR') : '';
  const extensionTypes = {
    xls: 'excel', xlsx: 'excel', csv: 'excel',
    doc: 'word', docx: 'word',
    ppt: 'powerpoint', pptx: 'powerpoint',
    pdf: 'pdf',
    jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', svg: 'image',
    mp4: 'video', mov: 'video', avi: 'video', webm: 'video',
    zip: 'archive', rar: 'archive', '7z': 'archive',
    txt: 'text'
  };
  const type = extensionTypes[extension] || (FILE_TYPE_VISUALS[item.type] ? item.type : 'other');
  const defaultVisual = FILE_TYPE_VISUALS[type] || FILE_TYPE_VISUALS.other;

  return {
    type,
    symbol: defaultVisual.symbol,
    label: extension ? extension.toLocaleUpperCase('tr-TR').slice(0, 4) : defaultVisual.label
  };
};

const DocumentsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 350);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0, limit: DOCUMENTS_PER_PAGE });
  const [categoryCounts, setCategoryCounts] = useState(EMPTY_CATEGORY_COUNTS);
  const [folderCount, setFolderCount] = useState(0);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState('single');
  const [draggedDocumentId, setDraggedDocumentId] = useState('');
  const [dragOverFolderId, setDragOverFolderId] = useState('');
  const [movingDocumentId, setMovingDocumentId] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingDocument, setEditingDocument] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    files: [],
    category: 'other'
  });
  const hasLoadedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const { toasts, showToast, removeToast } = useToast();
  const documentAccess = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userData') || '{}');
      const isAdmin = ['super_admin', 'company_admin'].includes(user.role);
      return {
        create: isAdmin || hasPermission(user, 'documents', 'create'),
        edit: isAdmin || hasPermission(user, 'documents', 'edit'),
        delete: isAdmin || hasPermission(user, 'documents', 'delete')
      };
    } catch (error) { return { create: false, edit: false, delete: false }; }
  }, []);
  const createSampleDocuments = useCallback(async () => {
    const sampleDocuments = [
      {
        name: 'İnsan Kaynakları Politikaları',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Kalite Yönetim Sistemleri',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Proje Şablonları',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'forms'
      },
      {
        name: 'İş Sağlığı ve Güvenliği',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Yıllık İzin Prosedürü.pdf',
        type: 'pdf',
        size: '1.2 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'ISO_9001_Sertifikası.pdf',
        type: 'pdf',
        size: '850 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'Çalışan_El_Kitabı.docx',
        type: 'word',
        size: '2.5 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-doc',
        color: 'text-primary',
        category: 'corporate'
      },
      {
        name: 'Organizasyon_Şeması.xlsx',
        type: 'excel',
        size: '890 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-spreadsheet',
        color: 'text-success',
        category: 'corporate'
      },
      {
        name: 'Şirket_Sunumu.pptx',
        type: 'powerpoint',
        size: '15.3 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-export',
        color: 'text-orange',
        category: 'corporate'
      },
      {
        name: 'Güvenlik_Politikası.pdf',
        type: 'pdf',
        size: '675 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'Masraf_Bildirim_Formu.xlsx',
        type: 'excel',
        size: '420 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-spreadsheet',
        color: 'text-success',
        category: 'forms'
      },
      {
        name: 'Dilekçe_Örneği.docx',
        type: 'word',
        size: '310 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-doc',
        color: 'text-primary',
        category: 'forms'
      }
    ];

    // Eski örnek veri tanımları ağ hatasında kullanıcıya gerçek dokümanmış gibi
    // gösterilmez; liste güvenli biçimde boş kalır.
    void sampleDocuments;
    setDocuments([]);
    setPagination({ current: 1, pages: 1, total: 0, limit: DOCUMENTS_PER_PAGE });
    setCategoryCounts(EMPTY_CATEGORY_COUNTS);
  }, []);

  const fetchDocuments = useCallback(async () => {
    const requestId = ++requestSequenceRef.current;
    try {
      if (hasLoadedRef.current) setRefreshing(true);
      else setLoading(true);
      const params = {
        page: currentPage,
        limit: DOCUMENTS_PER_PAGE
      };

      if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim();
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (currentFolderId) params.parentFolder = currentFolderId;

      const response = await api.getDocuments(params);
      if (requestId !== requestSequenceRef.current) return;
      setDocuments(response.data || []);
      setPagination(response.pagination || { current: currentPage, pages: 1, total: 0, limit: DOCUMENTS_PER_PAGE });
      setCategoryCounts(response.categoryCounts || EMPTY_CATEGORY_COUNTS);
      setFolderCount(response.folderCount || 0);
      setCurrentFolder(response.currentFolder || null);
      setBreadcrumbs(response.breadcrumbs || []);
    } catch (error) {
      if (requestId !== requestSequenceRef.current) return;
      console.error('Dokümanlar yüklenirken hata:', error);
      await createSampleDocuments();
    } finally {
      if (requestId === requestSequenceRef.current) {
        hasLoadedRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [createSampleDocuments, currentFolderId, currentPage, debouncedSearchTerm, selectedCategory]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    setCurrentPage(1);
  }, [currentFolderId, debouncedSearchTerm, selectedCategory]);

  useEffect(() => {
    const requestedCategory = searchParams.get('kategori');
    const matchedCategory = documentCategories.find(
      (category) => category.queryValue === requestedCategory || category.legacyQueryValues?.includes(requestedCategory)
    );

    setSelectedCategory(matchedCategory?.id || 'all');
  }, [searchParams]);

  const inferCategory = useCallback((document) => {
    if (documentCategories.some((category) => category.id === document.category)) {
      return document.category;
    }

    const searchableText = `${document.name || ''} ${document.description || ''}`.toLocaleLowerCase('tr-TR');
    const matchedCategory = documentCategories.find(
      (category) =>
        category.id !== 'all' &&
        category.keywords.some((keyword) => searchableText.includes(keyword.toLocaleLowerCase('tr-TR')))
    );

    return matchedCategory?.id || 'other';
  }, []);

  const documentsWithCategory = useMemo(
    () => documents.map((document) => ({ ...document, resolvedCategory: inferCategory(document) })),
    [documents, inferCategory]
  );

  const filteredDocuments = documentsWithCategory;
  const folderItems = useMemo(
    () => filteredDocuments.filter((item) => item.type === 'folder'),
    [filteredDocuments]
  );
  const fileItems = useMemo(
    () => filteredDocuments.filter((item) => item.type !== 'folder'),
    [filteredDocuments]
  );
  const activeCategory = useMemo(
    () => documentCategories.find((category) => category.id === selectedCategory) || documentCategories[0],
    [selectedCategory]
  );

  const visiblePageNumbers = useMemo(() => {
    if (pagination.pages <= 7) {
      return Array.from({ length: pagination.pages }, (_, index) => index + 1);
    }

    const candidates = [1, currentPage - 1, currentPage, currentPage + 1, pagination.pages]
      .filter((page) => page >= 1 && page <= pagination.pages);
    const uniquePages = [...new Set(candidates)].sort((left, right) => left - right);
    const pageItems = [];

    uniquePages.forEach((page, index) => {
      if (index > 0 && page - uniquePages[index - 1] > 1) pageItems.push(`ellipsis-${page}`);
      pageItems.push(page);
    });

    return pageItems;
  }, [currentPage, pagination.pages]);

  const setCategoryFromCard = (categoryId) => {
    setCurrentPage(1);
    setCurrentFolderId(null);
    setCurrentFolder(null);
    setBreadcrumbs([]);
    setSelectedCategory(categoryId);
    const category = documentCategories.find((item) => item.id === categoryId);

    if (!category || category.id === 'all') {
      setSearchParams({});
      return;
    }

    setSearchParams({ kategori: category.queryValue });
  };

  const formatFileSize = (size) => {
    if (!size) return '-';
    return size;
  };

  const formatModifiedDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
  };

  const getCategoryLabel = (categoryId) =>
    documentCategories.find((category) => category.id === categoryId)?.shortLabel || 'Diğer';

  const getFileTypeLabel = (type) => {
    const types = {
      folder: 'Klasör',
      pdf: 'PDF Belgesi',
      word: 'Word Belgesi',
      excel: 'Excel Belgesi',
      powerpoint: 'PowerPoint Sunumu',
      image: 'Resim',
      video: 'Video',
      other: 'Diğer'
    };

    return types[type] || 'Bilinmeyen';
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: 'bx-file-pdf',
      doc: 'bx-file-doc',
      docx: 'bx-file-doc',
      xls: 'bx-file',
      xlsx: 'bx-file',
      ppt: 'bx-file',
      pptx: 'bx-file',
      jpg: 'bx-image',
      jpeg: 'bx-image',
      png: 'bx-image',
      mp4: 'bx-video',
      gif: 'bx-image',
      txt: 'bx-file-txt',
      zip: 'bx-archive',
      rar: 'bx-archive'
    };

    return iconMap[ext] || 'bx-file';
  };

  const getActiveCategory = () => currentFolder?.category || (
    selectedCategory !== 'all' ? selectedCategory : 'other'
  );

  const openUploadModal = (mode) => {
    setUploadMode(mode);
    setUploadForm({ title: '', description: '', files: [], category: getActiveCategory() });
    setShowUploadModal(true);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploadForm((prev) => ({
      ...prev,
      files,
      title: files.length === 1 ? files[0].name : ''
    }));
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.files.length) {
      showToast('Lütfen en az bir dosya seçin', 'error');
      return;
    }

    if (uploadMode === 'single' && uploadForm.files.length === 1 && !uploadForm.title.trim()) {
      showToast('Lütfen dosya başlığı girin', 'error');
      return;
    }

    if (uploadForm.files.some((file) => file.size > 1024 * 1024 * 1024)) {
      showToast("Dosya boyutu 1 GB'dan büyük olamaz", 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }

          return prev + 10;
        });
      }, 100);

      if (uploadMode === 'single' && uploadForm.files.length === 1) {
        await api.uploadDocument(
          uploadForm.files[0],
          uploadForm.title,
          uploadForm.description,
          {
            category: uploadForm.category,
            parentFolder: currentFolderId
          }
        );
      } else {
        await api.uploadDocuments(uploadForm.files, {
          category: uploadForm.category,
          parentFolder: currentFolderId,
          description: uploadForm.description
        });
      }

      clearInterval(progressInterval);
      setUploadProgress(100);
      if (currentPage === 1) {
        await fetchDocuments();
      } else {
        setCurrentPage(1);
      }
      const uploadedCount = uploadForm.files.length;
      setUploadForm((current) => ({ ...current, title: '', description: '', files: [], category: 'other' }));
      setShowUploadModal(false);

      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        showToast(uploadedCount > 1 ? `${uploadedCount} dosya başarıyla yüklendi!` : 'Dosya başarıyla yüklendi!', 'success');
      }, 500);
    } catch (error) {
      console.error('Dosya yüklenirken hata:', error);
      showToast(`Dosya yüklenirken hata oluştu: ${error.message}`, 'error');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreateFolder = async (event) => {
    event.preventDefault();
    if (!newFolderName.trim()) return;
    try {
      await api.createDocumentFolder({
        name: newFolderName.trim(),
        parentFolder: currentFolderId,
        category: getActiveCategory()
      });
      setNewFolderName('');
      setShowFolderModal(false);
      await fetchDocuments();
      showToast('Klasör oluşturuldu.', 'success');
    } catch (error) {
      showToast(error.message || 'Klasör oluşturulamadı.', 'error');
    }
  };

  const openFolder = (folder) => {
    setCurrentFolderId(folder._id || folder.id);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const navigateToFolder = (folderId) => {
    setCurrentFolderId(folderId || null);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleDocumentDragStart = (event, item) => {
    if (!documentAccess.edit || item.type === 'folder' || event.target.closest('.documents-file-actions')) {
      event.preventDefault();
      return;
    }

    const documentId = String(item._id || item.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', documentId);
    setDraggedDocumentId(documentId);
  };

  const handleDocumentDragEnd = () => {
    setDraggedDocumentId('');
    setDragOverFolderId('');
  };

  const handleFolderDragOver = (event, folderId) => {
    if (!documentAccess.edit || !draggedDocumentId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(String(folderId));
  };

  const handleFolderDragLeave = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragOverFolderId('');
  };

  const handleFolderDrop = async (event, folder) => {
    event.preventDefault();
    const documentId = event.dataTransfer.getData('text/plain') || draggedDocumentId;
    const folderId = String(folder._id || folder.id);
    const movedDocument = fileItems.find((item) => String(item._id || item.id) === String(documentId));

    setDraggedDocumentId('');
    setDragOverFolderId('');
    if (!documentAccess.edit || !documentId || !movedDocument) return;

    try {
      setMovingDocumentId(String(documentId));
      await api.updateDocument(documentId, { parentFolder: folderId });
      if (fileItems.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        await fetchDocuments();
      }
      showToast(`"${movedDocument.name}" dosyası "${folder.name}" klasörüne taşındı.`, 'success');
    } catch (error) {
      showToast(error.message || 'Dosya klasöre taşınamadı.', 'error');
    } finally {
      setMovingDocumentId('');
    }
  };

  const handleDownload = async (documentId) => {
    const selectedDocument = documents.find((item) => (item._id || item.id) === documentId);

    if (selectedDocument?.type === 'folder') {
      openFolder(selectedDocument);
      return;
    }

    try {
      const url = await api.getDocumentDownloadUrl(documentId);
      const link = document.createElement('a');

      link.href = url;
      link.download = selectedDocument?.originalName || selectedDocument?.name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('Dosya indirme işlemi başlatıldı.', 'success');
    } catch (error) {
      console.error('Dosya indirilirken hata:', error);
      showToast(`Dosya indirilirken hata oluştu: ${error.message}`, 'error');
    }
  };

  const handleDelete = async (documentId, documentName) => {
    showToast(`"${documentName}" öğesini silmek istediğinizden emin misiniz?`, 'warning', {
      duration: 0,
      actions: [
        {
          label: 'Evet, Sil',
          onClick: async () => {
            try {
              await api.deleteDocument(documentId);
              if (documents.length === 1 && currentPage > 1) {
                setCurrentPage((page) => page - 1);
              } else {
                await fetchDocuments();
              }
              showToast('Öğe başarıyla silindi!', 'success');
            } catch (error) {
              console.error('Dosya silinirken hata:', error);
              showToast(`Öğe silinirken hata oluştu: ${error.message}`, 'error');
            }
          }
        },
        {
          label: 'İptal',
          onClick: () => {}
        }
      ]
    });
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    try {
      const response = await api.updateDocument(editingDocument._id, {
        name: editingDocument.name,
        title: editingDocument.name,
        description: editingDocument.description || '',
        category: editingDocument.category || editingDocument.resolvedCategory || 'other'
      });
      setDocuments((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setEditingDocument(null);
      showToast('Doküman bilgileri güncellendi.', 'success');
    } catch (error) { showToast(error.message || 'Doküman güncellenemedi.', 'error'); }
  };

  if (loading) {
    return (
      <div className="modern-loading">
        <div className="text-center">
          <div className="modern-spinner spinner-border mb-3" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <p className="text-muted">Dokümanlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <header className="documents-page-header">
        <div className="documents-page-heading">
          <span className="documents-page-heading-icon" aria-hidden="true">
            <i className="bx bx-library" />
          </span>
          <div>
            <p>Kurumsal kaynaklar</p>
            <h1>Doküman Merkezi</h1>
            <span>Dosya ve klasörleri kategori yapısı içinde bulun, görüntüleyin ve yönetin.</span>
          </div>
        </div>
        <div className="documents-page-summary" aria-label="Doküman merkezi özeti">
          <span><strong>{folderCount}</strong> klasör</span>
          <span><strong>{pagination.total}</strong> doküman</span>
          <button type="button" onClick={() => navigate(ROUTE_PATHS.announcements)}>
            Duyurular <i className="bx bx-right-arrow-alt" aria-hidden="true" />
          </button>
        </div>
      </header>

      <section className="documents-browser-controls" aria-label="Doküman filtreleri ve işlemleri">
        {!currentFolder && (
          <div className="documents-category-rail" role="tablist" aria-label="Doküman kategorileri">
            {documentCategories.map((category) => (
              <button
                type="button"
                role="tab"
                aria-selected={selectedCategory === category.id}
                key={category.id}
                className={`documents-category-tab ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setCategoryFromCard(category.id)}
              >
                <i className={category.icon} aria-hidden="true" />
                <span>{category.shortLabel}</span>
                <small>{categoryCounts[category.id] || 0}</small>
              </button>
            ))}
          </div>
        )}

        <div className="documents-location-bar">
          <div className="documents-location-copy">
            {currentFolder && (
              <button
                type="button"
                className="documents-up-button"
                onClick={() => navigateToFolder(currentFolder.parentFolder || null)}
              >
                <i className="bx bx-left-arrow-alt" aria-hidden="true" />
                <span>Üst klasör</span>
              </button>
            )}
            <nav className="documents-breadcrumb" aria-label="Doküman klasör yolu">
              <button type="button" onClick={() => navigateToFolder(null)}>
                <i className="bx bx-home-alt" aria-hidden="true" /> Ana dizin
              </button>
              {breadcrumbs.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <i className="bx bx-chevron-right" aria-hidden="true" />
                  {index === breadcrumbs.length - 1 ? (
                    <span aria-current="page">{folder.name}</span>
                  ) : (
                    <button type="button" onClick={() => navigateToFolder(folder.id)}>{folder.name}</button>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

          <label className="documents-search-field">
            <i className="bx bx-search" aria-hidden="true" />
            <input
              type="search"
              placeholder="Dosya veya klasör ara"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} aria-label="Aramayı temizle">
                <i className="bx bx-x" aria-hidden="true" />
              </button>
            )}
          </label>

          {documentAccess.create && (
            <div className="documents-create-actions">
              <button type="button" className="documents-action-secondary" onClick={() => setShowFolderModal(true)}>
                <i className="bx bx-folder-plus" aria-hidden="true" /> Yeni klasör
              </button>
              <button type="button" className="documents-action-secondary" onClick={() => openUploadModal('folder')} disabled={uploading}>
                <i className="bx bx-folder-open" aria-hidden="true" /> Klasör yükle
              </button>
              <button type="button" className="documents-action-primary" onClick={() => openUploadModal('single')} disabled={uploading}>
                <i className="bx bx-upload" aria-hidden="true" /> Dosya yükle
              </button>
            </div>
          )}
        </div>
      </section>

      {uploading && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="modern-card p-4">
              <div className="d-flex align-items-center">
                <div className="modern-spinner spinner-border-sm me-3" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-medium">Dosya yükleniyor...</span>
                    <span className="text-muted">{uploadProgress}%</span>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="documents-content search-results-region" aria-busy={refreshing}>
        <header className="documents-content-header">
          <div>
            <span>{searchTerm ? 'Arama sonuçları' : currentFolder ? 'Klasör içeriği' : 'İçerikler'}</span>
            <h2>{currentFolder?.name || activeCategory.title}</h2>
          </div>
          <p>{folderItems.length} klasör · {fileItems.length} dosya gösteriliyor</p>
        </header>

        {refreshing && (
          <div className="search-refresh-indicator" role="status">
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            Sonuçlar güncelleniyor…
          </div>
        )}

        {folderItems.length > 0 && (
          <div className="documents-folder-section">
            <div className="documents-section-title">
              <h3><i className="bx bxs-folder" aria-hidden="true" /> Klasörler</h3>
              <span>{folderItems.length}</span>
              {documentAccess.edit && fileItems.length > 0 && (
                <p className="documents-drag-tip"><i className="bx bx-move" aria-hidden="true" /> Dosyayı klasöre sürükleyerek taşıyabilirsiniz</p>
              )}
            </div>
            <div className="documents-folder-grid">
              {folderItems.map((folder) => (
                <article
                  className={`documents-folder-card ${dragOverFolderId === String(folder._id || folder.id) ? 'is-drop-target' : ''}`}
                  key={folder._id || folder.id}
                  onDragOver={(event) => handleFolderDragOver(event, folder._id || folder.id)}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={(event) => handleFolderDrop(event, folder)}
                >
                  <button type="button" className="documents-folder-open" onClick={() => openFolder(folder)}>
                    <span className="documents-folder-icon"><i className="bx bxs-folder" aria-hidden="true" /></span>
                    <span className="documents-folder-copy">
                      <strong>{folder.name}</strong>
                      <small>{dragOverFolderId === String(folder._id || folder.id) ? 'Buraya bırakarak taşı' : folder.description || 'Klasörü aç'}</small>
                    </span>
                    <i className="bx bx-chevron-right" aria-hidden="true" />
                  </button>
                  {(documentAccess.edit || documentAccess.delete) && (
                    <div className="documents-folder-actions">
                      {documentAccess.edit && (
                        <button type="button" onClick={() => setEditingDocument(folder)} aria-label={`${folder.name} klasörünü düzenle`}>
                          <i className="bx bx-edit" aria-hidden="true" />
                        </button>
                      )}
                      {documentAccess.delete && (
                        <button type="button" className="is-danger" onClick={() => handleDelete(folder._id || folder.id, folder.name)} aria-label={`${folder.name} klasörünü sil`}>
                          <i className="bx bx-trash" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        )}

        {fileItems.length > 0 && (
          <div className="documents-file-section">
            <div className="documents-section-title">
              <h3><i className="bx bx-file" aria-hidden="true" /> Dosyalar</h3>
              <span>{fileItems.length}</span>
            </div>
            <div className="documents-file-list" role="list">
              <div className="documents-file-list-head" aria-hidden="true">
                <span>Dosya</span><span>Kategori / boyut</span><span>Güncelleme</span><span>İşlemler</span>
              </div>
              {fileItems.map((item) => {
                const visual = getVisualFileType(item);
                const itemId = String(item._id || item.id);

                return <article
                  className={`documents-file-row ${documentAccess.edit ? 'can-drag' : ''} ${draggedDocumentId === itemId ? 'is-dragging' : ''} ${movingDocumentId === itemId ? 'is-moving' : ''}`}
                  role="listitem"
                  key={itemId}
                  draggable={documentAccess.edit && movingDocumentId !== itemId}
                  onDragStart={(event) => handleDocumentDragStart(event, item)}
                  onDragEnd={handleDocumentDragEnd}
                >
                  <button
                    type="button"
                    className="documents-file-main"
                    onClick={() => handleDownload(item._id || item.id)}
                    aria-label={`${item.name} dosyasını indir`}
                  >
                    {documentAccess.edit && (
                      <span className="documents-drag-handle" title="Klasöre taşımak için sürükleyin" aria-hidden="true">
                        <i className="bx bx-grid-vertical" />
                      </span>
                    )}
                    <span className={`documents-file-icon type-${visual.type}`} aria-hidden="true">
                      <strong>{visual.symbol}</strong>
                      <small>{visual.label}</small>
                    </span>
                    <span className="documents-file-copy">
                      <strong>{item.name}</strong>
                      <small>{item.description || getFileTypeLabel(item.type)}</small>
                    </span>
                  </button>
                  <div className="documents-file-meta">
                    <span>{getCategoryLabel(item.resolvedCategory)}</span>
                    <small>{formatFileSize(item.size)} · {getFileTypeLabel(item.type)}</small>
                  </div>
                  <time className="documents-file-date" dateTime={item.modified || undefined}>
                    {formatModifiedDate(item.modified)}
                  </time>
                  <div className="documents-file-actions">
                    <button type="button" className="is-primary" onClick={() => handleDownload(item._id || item.id)}>
                      <i className="bx bx-download" aria-hidden="true" /><span>İndir</span>
                    </button>
                    {documentAccess.edit && (
                      <button type="button" onClick={() => setEditingDocument(item)} aria-label={`${item.name} dosyasını düzenle`} title="Düzenle">
                        <i className="bx bx-edit" aria-hidden="true" />
                      </button>
                    )}
                    {documentAccess.delete && (
                      <button type="button" className="is-danger" onClick={() => handleDelete(item._id || item.id, item.name)} aria-label={`${item.name} dosyasını sil`} title="Sil">
                        <i className="bx bx-trash" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </article>;
              })}
            </div>
          </div>
        )}

        {filteredDocuments.length === 0 && (
          <div className="documents-empty-state">
            <span><i className={`bx ${searchTerm ? 'bx-search-alt' : 'bx-folder-open'}`} aria-hidden="true" /></span>
            <h3>{searchTerm ? 'Eşleşen içerik bulunamadı' : 'Bu klasör henüz boş'}</h3>
            <p>{searchTerm ? 'Farklı bir kelime deneyebilir veya aramayı temizleyebilirsiniz.' : 'Dosya yükleyebilir ya da yeni bir alt klasör oluşturabilirsiniz.'}</p>
            {searchTerm ? (
              <button type="button" className="documents-action-secondary" onClick={() => setSearchTerm('')}>Aramayı temizle</button>
            ) : documentAccess.create ? (
              <button type="button" className="documents-action-primary" onClick={() => openUploadModal('single')}>
                <i className="bx bx-upload" aria-hidden="true" /> Dosya yükle
              </button>
            ) : null}
          </div>
        )}

        {pagination.pages > 1 && (
          <nav className="documents-pagination" aria-label="Doküman sayfaları">
            <p className="documents-pagination-summary" aria-live="polite">
              {((currentPage - 1) * pagination.limit) + 1}–{Math.min(currentPage * pagination.limit, pagination.total)} / {pagination.total} doküman
            </p>
            <div className="documents-pagination-controls">
              <button type="button" onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))} disabled={currentPage === 1} aria-label="Önceki sayfa">
                <i className="bx bx-chevron-left" aria-hidden="true" /><span>Önceki</span>
              </button>
              <div className="documents-pagination-pages">
                {visiblePageNumbers.map((pageItem) => typeof pageItem === 'number' ? (
                  <button type="button" key={pageItem} className={pageItem === currentPage ? 'active' : ''} onClick={() => setCurrentPage(pageItem)} aria-label={`${pageItem}. sayfa`} aria-current={pageItem === currentPage ? 'page' : undefined}>
                    {pageItem}
                  </button>
                ) : <span key={pageItem} aria-hidden="true">…</span>)}
              </div>
              <button type="button" onClick={() => setCurrentPage((page) => Math.min(page + 1, pagination.pages))} disabled={currentPage === pagination.pages} aria-label="Sonraki sayfa">
                <span>Sonraki</span><i className="bx bx-chevron-right" aria-hidden="true" />
              </button>
            </div>
          </nav>
        )}
      </section>

      {showUploadModal && (
        <div className="modern-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modern-upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modern-upload-header">
              <div className="d-flex align-items-center">
                <div className="modern-upload-icon">
                  <i className="bx bx-cloud-upload"></i>
                </div>
                <div>
                  <h4 className="modern-upload-title mb-1">
                    {uploadMode === 'folder' ? 'Klasör Yükle' : 'Dosya Yükle'}
                  </h4>
                  <p className="modern-upload-subtitle">
                    {uploadMode === 'folder'
                      ? 'Seçtiğiniz klasör ve alt klasör yapısı korunur.'
                      : 'Bir veya birden fazla dosyayı sisteme ekleyin.'}
                  </p>
                </div>
              </div>
              <button className="modern-upload-close" onClick={() => setShowUploadModal(false)}>
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="modern-upload-body">
              <div className="modern-drop-zone">
                <div className="modern-drop-content">
                  <div className="modern-drop-icon">
                    <i className="bx bx-cloud-upload"></i>
                  </div>
                  <h5 className="modern-drop-title">
                    {uploadMode === 'folder' ? 'Yüklenecek klasörü seçin' : 'Yüklenecek dosyaları seçin'}
                  </h5>
                  <p className="modern-drop-text">Alt klasörler ve birden fazla dosya desteklenir</p>
                  <input
                    type="file"
                    className="modern-file-input"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.mp4"
                    id="fileInput"
                    multiple
                    {...(uploadMode === 'folder' ? { webkitdirectory: '', directory: '' } : {})}
                  />
                  <label htmlFor="fileInput" className="modern-file-label">
                    {uploadMode === 'folder' ? 'Klasör Seç' : 'Dosya Seç'}
                  </label>
                </div>
                <div className="modern-drop-info">
                  <small className="text-muted">
                    <i className="bx bx-info-circle me-1"></i>
                    PDF, Office, metin, CSV, JPG, PNG veya MP4 (Maks: 1 GB)
                  </small>
                </div>
              </div>

              {uploadForm.files.length > 0 && (
                <div className="modern-file-preview">
                  <div className="modern-file-card">
                    <div className="modern-file-icon-preview">
                      <i className={`bx ${uploadMode === 'folder' ? 'bx-folder' : getFileIcon(uploadForm.files[0].name)}`}></i>
                    </div>
                    <div className="modern-file-details">
                      <h6 className="modern-file-name">
                        {uploadForm.files.length === 1 ? uploadForm.files[0].name : `${uploadForm.files.length} dosya seçildi`}
                      </h6>
                      <p className="modern-file-size">
                        {(uploadForm.files.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      className="modern-file-remove"
                      onClick={() => setUploadForm((prev) => ({ ...prev, files: [], title: '' }))}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  </div>
                </div>
              )}

              <div className="modern-upload-form">
                <div className="modern-form-group">
                  <label className="modern-form-label" htmlFor="documentCategory">
                    <i className="bx bx-folder me-2"></i>
                    Kategori *
                  </label>
                  <select
                    id="documentCategory"
                    className="modern-form-input"
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, category: e.target.value }))}
                    disabled={Boolean(currentFolder)}
                  >
                    {documentCategories.filter((category) => category.id !== 'all').map((category) => (
                      <option key={category.id} value={category.id}>{category.title}</option>
                    ))}
                  </select>
                </div>

                {uploadMode === 'single' && uploadForm.files.length <= 1 && <div className="modern-form-group">
                  <label className="modern-form-label">
                    <i className="bx bx-edit me-2"></i>
                    Dosya Başlığı *
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Dosya başlığını girin..."
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </div>}

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <i className="bx bx-message-square-detail me-2"></i>
                    Açıklama
                  </label>
                  <textarea
                    className="modern-form-textarea"
                    rows="3"
                    placeholder="Dosya hakkında açıklama..."
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, description: e.target.value }))}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="modern-upload-footer">
              <button className="modern-btn-cancel" onClick={() => setShowUploadModal(false)}>
                <i className="bx bx-x me-1"></i>
                İptal
              </button>
              <button
                className="modern-btn-upload"
                onClick={handleUploadSubmit}
                disabled={
                  uploading ||
                  uploadForm.files.length === 0 ||
                  (uploadMode === 'single' && uploadForm.files.length === 1 && !uploadForm.title.trim())
                }
              >
                {uploading ? (
                  <>
                    <span className="modern-spinner me-2"></span>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <i className="bx bx-upload me-1"></i>
                    {uploadForm.files.length > 1 ? `${uploadForm.files.length} Dosyayı Yükle` : 'Yükle'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFolderModal && (
        <div className="modern-modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && setShowFolderModal(false)}>
          <form className="document-edit-dialog" onSubmit={handleCreateFolder}>
            <header>
              <div><span>Doküman merkezi</span><h3>Yeni klasör</h3></div>
              <button type="button" onClick={() => setShowFolderModal(false)} aria-label="Kapat"><i className="bx bx-x" /></button>
            </header>
            <label>
              <span>Klasör adı</span>
              <input
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                placeholder="Örn. Çevre Prosedürleri"
                autoFocus
                required
              />
            </label>
            <p className="text-muted small mb-0">
              {currentFolder ? `${currentFolder.name} klasörünün altında oluşturulacak.` : 'Seçili doküman kategorisinin kökünde oluşturulacak.'}
            </p>
            <footer>
              <button type="button" className="role-secondary-button" onClick={() => setShowFolderModal(false)}>Vazgeç</button>
              <button className="role-primary-button" type="submit">Klasörü Oluştur</button>
            </footer>
          </form>
        </div>
      )}

      {editingDocument && <div className="modern-modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && setEditingDocument(null)}><form className="document-edit-dialog" onSubmit={handleEditSubmit}><header><div><span>Doküman yönetimi</span><h3>Dokümanı düzenle</h3></div><button type="button" onClick={() => setEditingDocument(null)} aria-label="Kapat"><i className="bx bx-x" /></button></header><label><span>Başlık</span><input value={editingDocument.name || ''} onChange={(event) => setEditingDocument({ ...editingDocument, name: event.target.value })} required /></label><label><span>Kategori</span><select value={editingDocument.category || editingDocument.resolvedCategory || 'other'} onChange={(event) => setEditingDocument({ ...editingDocument, category: event.target.value })}>{documentCategories.filter((category) => category.id !== 'all').map((category) => <option key={category.id} value={category.id}>{category.title}</option>)}</select></label><label><span>Açıklama</span><textarea rows="4" value={editingDocument.description || ''} onChange={(event) => setEditingDocument({ ...editingDocument, description: event.target.value })} /></label><footer><button type="button" className="role-secondary-button" onClick={() => setEditingDocument(null)}>Vazgeç</button><button className="role-primary-button" type="submit">Kaydet</button></footer></form></div>}

    </div>
  );
};

export default DocumentsPage;
